/**
 * Recipe PDF print template (parity: RedConcierge/static/templates/recipe_print_template.html).
 * Accent colors are injected via {{ACCENT_COLOR}} / soft tints from wine-drawer.js
 * so each winery matches its agent recipe UI (not a hardcoded Wine Engine maroon).
 */
export default `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>{{RECIPE_TITLE}}</title>
  <link rel="preconnect" href="https://fonts.googleapis.com">
  <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
  <link href="https://fonts.googleapis.com/css2?family=Manrope:wght@700&family=Open+Sans:wght@400;600;700&display=swap" rel="stylesheet">
  <style>
    @page {
      size: A4;
      margin: 0;
    }

    :root {
      --color-primary: {{ACCENT_COLOR}};
      --color-primary-soft: {{ACCENT_SOFT_BG}};
      --color-primary-border: {{ACCENT_SOFT_BORDER}};
      --color-bullet: {{ACCENT_BULLET}};
    }

    *, *::before, *::after {
      box-sizing: border-box;
    }

    html, body {
      margin: 0;
      padding: 0;
      width: 100%;
    }

    body {
      font-family: 'Open Sans', sans-serif;
      color: #1d1d1d;
      line-height: 1.5;
      font-size: 14px;
      background-color: #fff;
      -webkit-font-smoothing: antialiased;
      -moz-osx-font-smoothing: grayscale;
      text-rendering: optimizeLegibility;
    }

    .recipe-print-container {
      max-width: 100%;
      width: 100%;
      margin: 0;
      padding: 8px 4px 24px;
      overflow: visible;
    }

    .print-footer {
      display: flex;
      justify-content: space-between;
      align-items: center;
      border-top: 1px solid #e5e5e5;
      font-size: 11px;
      color: #666;
      background: #fff;
      /* Extra bottom padding so html2canvas does not clip the descenders /
         logo under the page edge (ss: only top of "powered by" visible). */
      padding: 14px 0 20px;
      margin-top: 24px;
      /* Keep label + brand + logo together, but ALLOW the whole footer to
         move to the next page when the current page is full. page-break-before:
         avoid was squeezing it into the clipped bottom strip. */
      page-break-inside: avoid;
      break-inside: avoid;
    }

    .print-footer-left {
      display: flex;
      align-items: center;
      gap: 6px;
      flex-wrap: wrap;
      min-height: 20px;
    }

    .print-footer-label {
      color: #666;
      font-size: 11px;
      font-weight: 400;
      line-height: 1.3;
    }

    .print-footer-logo {
      height: 14px;
      width: 14px;
      vertical-align: middle;
      border-radius: 50%;
      object-fit: cover;
      display: inline-block;
    }

    .print-footer-brand {
      /* Solid readable color — light winery accents (e.g. Goose gold) made
         the brand name disappear on white when using --color-primary here. */
      color: {{FOOTER_BRAND_COLOR}};
      text-decoration: none;
      font-weight: 600;
      font-size: 11px;
      line-height: 1.3;
      display: inline-flex;
      align-items: center;
      gap: 4px;
    }

    /* Safety buffer below footer for html2pdf page slicing. */
    .print-footer-spacer {
      height: 16px;
      page-break-inside: avoid;
      break-inside: avoid;
    }

    .print-header {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      padding-bottom: 12px;
      border-bottom: 1px solid #f1f1f1;
      margin-bottom: 24px;
    }

    .print-header-icon {
      color: var(--color-primary);
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .print-header-icon svg {
      width: 22px;
      height: 22px;
    }

    .print-header-title {
      font-size: 18px;
      font-weight: 700;
      color: var(--color-primary);
      margin: 0;
      letter-spacing: 0.05em;
      text-transform: uppercase;
    }

    .recipe-title {
      font-size: 24px;
      font-weight: 700;
      color: var(--color-primary);
      margin: 0 0 8px;
    }

    .recipe-detail-descriptor {
      font-size: 13.5px;
      line-height: 1.45;
      color: #666;
      margin: 0 0 24px;
    }

    .recipe-section-title {
      margin: 24px 0 10px;
      font-size: 14px;
      font-weight: 700;
      letter-spacing: 0.04em;
      text-transform: uppercase;
      color: var(--color-primary);
      border-bottom: 1px solid #f0f0f0;
      padding-bottom: 4px;
      page-break-after: avoid;
      break-after: avoid;
    }

    /* Match agent .ws-drawer-ingredients rows (name left, amount right). */
    .recipe-ingredients-list {
      margin: 0 0 24px;
      padding: 0;
      page-break-inside: avoid;
      break-inside: avoid;
    }

    .recipe-ingredient-row {
      display: flex;
      justify-content: space-between;
      align-items: baseline;
      padding: 8px 0;
      border-bottom: 1px solid #f0f0f0;
      font-size: 13.5px;
      line-height: 1.4;
      color: #1d1d1d;
      page-break-inside: avoid;
      break-inside: avoid;
    }

    .recipe-ingredient-name {
      color: #1d1d1d;
      flex: 1 1 auto;
      min-width: 0;
      padding-right: 12px;
    }

    .recipe-ingredient-bullet {
      color: var(--color-bullet);
      margin-right: 8px;
      font-size: 18px;
      line-height: 0;
      vertical-align: middle;
    }

    .recipe-ingredient-amount {
      color: var(--color-primary);
      font-weight: 600;
      white-space: nowrap;
      margin-left: 12px;
      flex-shrink: 0;
    }

    .recipe-method-list {
      margin: 0 0 24px;
      padding-left: 0;
      list-style: none;
    }

    .recipe-method-step {
      margin-bottom: 12px;
      display: flex;
      gap: 12px;
      font-size: 13.5px;
      align-items: flex-start;
      page-break-inside: avoid;
      break-inside: avoid;
    }

    .recipe-method-step-num {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 22px;
      height: 22px;
      border-radius: 50%;
      background: var(--color-primary);
      color: #fff;
      font-size: 11px;
      font-weight: 700;
      flex-shrink: 0;
      margin-top: 1px;
    }

    .recipe-pairing-box {
      background: var(--color-primary-soft);
      border: 1px solid var(--color-primary-border);
      border-radius: 10px;
      padding: 14px 16px;
      margin-top: 24px;
      page-break-inside: avoid;
      break-inside: avoid;
    }

    .recipe-pairing-box-title {
      margin: 0 0 6px;
      font-size: 12px;
      font-weight: 700;
      letter-spacing: 0.03em;
      text-transform: uppercase;
      color: var(--color-primary);
    }

    .recipe-pairing-box-text {
      margin: 0;
      font-size: 13.5px;
      line-height: 1.45;
      color: #1d1d1d;
    }
  </style>
</head>
<body>
  <div class="recipe-print-container">
    <div class="print-header">
      <span class="print-header-icon">
        <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 20 20" fill="none">
          <path d="M18.948 9.47468H16.8429V6.84252C16.8429 4.69989 15.2322 2.92638 13.1588 2.66867V2.63215C13.1588 2.05282 12.685 1.57909 12.1057 1.57909H6.82659C6.1999 0.595128 5.12614 0 3.94765 0H0.526017C0.236859 0 0 0.236863 0 0.526027C0 0.81519 0.236859 1.05205 0.526017 1.05205H3.94765C4.83685 1.05205 5.64811 1.5465 6.05273 2.34691C6.14254 2.52061 6.32117 2.63114 6.52152 2.63114H9.47449V4.73626H6.52152C6.32118 4.73626 6.14254 4.8468 6.05273 5.0205C5.77342 5.57318 5.29478 5.97288 4.73718 6.17323V3.15704C4.73718 2.57771 4.26346 2.10398 3.68414 2.10398H0.526017C0.236859 2.10398 0 2.34084 0 2.63001C0 2.91917 0.236859 3.15603 0.526017 3.15603H3.68414V6.31421H0.526017C0.236859 6.31421 0 6.55108 0 6.84024C0 7.1294 0.236859 7.36627 0.526017 7.36627H3.94765C5.12702 7.36627 6.20078 6.77114 6.82659 5.78718H12.1057C12.4689 5.78718 12.7896 5.60262 12.9791 5.32431C13.3897 5.46643 13.6848 5.85628 13.6848 6.31421V9.47239C13.1054 9.47239 12.6317 9.94612 12.6317 10.5255V13.1576C12.6317 15.0101 14.0055 16.5419 15.7898 16.8004V18.9479H14.7368C14.4476 18.9479 14.2108 19.1848 14.2108 19.474C14.2108 19.7631 14.4476 20 14.7368 20H17.8949C18.1841 20 18.4209 19.7631 18.4209 19.474C18.4209 19.1848 18.1841 18.9479 17.8949 18.9479H16.8419V16.8004C18.6262 16.5428 20 15.0111 20 13.1576V10.5255C20 9.94612 19.5263 9.47239 18.947 9.47239L18.948 9.47468Z" fill="currentColor"/>
        </svg>
      </span>
      <h2 class="print-header-title">Recipes</h2>
    </div>

    <h1 class="recipe-title">{{RECIPE_TITLE}}</h1>
    {{RECIPE_DESCRIPTOR}}
    {{INGREDIENTS_HTML}}
    {{METHOD_HTML}}
    {{PAIRING_HTML}}

    <div class="print-footer">
      <div class="print-footer-left">
        <span class="print-footer-label">powered by</span>
        <a href="{{POWERED_BRAND_URL}}" target="_blank" class="print-footer-brand">
          <span>{{POWERED_BRAND}}</span>
          {{POWERED_LOGO_HTML}}
        </a>
      </div>
    </div>
    <div class="print-footer-spacer" aria-hidden="true"></div>
  </div>
</body>
</html>
`;
